import { TYPES } from "@/types";
import { myContainer } from "@/inversify.config";
import { IEmploymentService } from "@/interfaces/IEmploymentService";
import * as dtos from "@/models/dtos";
import { FormBuilder2 } from "@/models/FormBuilder2";
import { ApplicantForm } from "@/models/CheckboxGroup";
import router from "@/router";
import { PositionUser } from "@/models/PositionUser";
import { MainContainer } from "../../models/MainContainer";

import {
  SendEmailToOneRecipientRequest,
  DynamicProperty,
} from "../../models/dtos";
import { SendEmailRequest } from "../../models/dtos";
import { ToastNotification } from "@/models/ToastNotification";
import { PositionFormTemplate } from "@/models/FormTemplate";

import _ from "lodash";

import { PositionTemplateAsset } from "@/models/PositionTemplateFolder";
import { getCurrentUnixTime, getYesterday } from "@/utils/DateTimezone";
const client = myContainer.get<IEmploymentService>(TYPES.IEmploymentService);

export default {
  state: {
    positionApplicant: {} as ApplicantForm,
    positions: [] as Array<dtos.PositionResponse>,
    positionApplicants: [] as Array<dtos.ApplicantPositionResponse>,
    position: {} as dtos.PositionResponse,
    positionUsers: [] as Array<PositionUser>,
    lastPositionLoaded: 0,
    positionsCard: [] as Array<dtos.PositionResponse>,
    positionTemplate: {} as PositionFormTemplate,
    positionTemplates: [] as Array<PositionFormTemplate>,
    positionFilters: [] as Array<dtos.PositionFilter>,
    positionSortings: [] as Array<dtos.SortApplicantsInPosition>,
    positionApplicantsToRender: 30,
    // Ordered applicant ids as currently filtered+sorted in the list view, so the
    // detail view's prev/next can step through the same subset. ANSKNI-97.
    filteredApplicantOrder: [] as Array<number>,
  },

  getters: {
    positionsCard(state: any) {
      return state.positionsCard;
    },
    positionUsers(state: any) {
      return state.positionUsers;
    },
    positionUserPermissionMatrix(state: any): Array<PositionUser> {
      return state.positionUsers.map((px: PositionUser) => {
        return px.getPermissionMatrix();
      });
    },
    positionApplicant(state: any) {
      return state.positionApplicant;
    },
    positionPermissions(state: any) {
      return "";
    },
    getApplicantformBuilderItems(state: any) {
      return state.positionApplicant.Items?.filter(
        (px: any) => px.item.type !== "checkbox"
      );
    },
    getApplicantFormBuilderAllItems(state: any) {
      return state.positionApplicant?.Items?.map((px: any) => px);
    },
    positions: (state: any) => state.positions,
    positionApplicants: (state: any) => state.positionApplicants,
    filteredApplicantOrder: (state: any) => state.filteredApplicantOrder,
    position: (state: any) => state.position,
    getPositionTemplate: (state: any) => state.positionTemplate,
    getPositionTemplates: (state: any) => state.positionTemplates,
    getPositionDefaultFilter: (state: any) => {
      const filter = state.positionFilters.find(
        (px: dtos.PositionFilter) => px.positionId === state.position.id
      )?.defaultFilter;

      return filter;
    },
    getPositionFilter: (state: any) => {
      const filter = state?.positionFilters?.find(
        (px: dtos.PositionFilter) => px?.positionId === state?.position?.id
      )?.currentFilter;

      return filter;
    },
    isFilterDefault: (state: any) => {
      const positionFilter = state?.positionFilters?.find(
        (px: dtos.PositionFilter) => px?.positionId === state?.position?.id
      ) as dtos.PositionFilter;

      if (!positionFilter) return null;

      const currentFilter = positionFilter?.currentFilter;
      const defaultFilter = positionFilter?.defaultFilter;

      return _.isEqual(currentFilter, defaultFilter);
    },
    getCurrentApplicantSortInPosition: (state: any) => {
      return state.positionSortings.find(
        (px: dtos.SortApplicantsInPosition) =>
          px?.positionId === state?.position?.id
      )?.currentSort;
    },
    getPositionByContainerId: (state: any) => (containerId: number) => {
      return state.positions.filter(
        (px: dtos.PositionResponse) => px.containerId === containerId
      );
    },
    getPositionApplicantsByLabelId: (state: any) => (labelId: number) => {
      return state.positionApplicants.filter(
        (px: dtos.ApplicantPositionResponse) =>
          px.labels.find((py: dtos.LabelResponse) => py.id === labelId)
      );
    },
    applicantsToRender: (state: any) => state.positionApplicantsToRender,
    getFilterDataFromMetadata: (state: any) => (filterKey: string) => {
      const keys = state.position.metadata.properties.find(
        (item: any) => item.key === filterKey
      );

      if (!keys) return [];

      let filterData = [] as dtos.DynamicProperty[];

      keys.data.keys.forEach((item: any) => {
        filterData = [
          ...filterData,
          state.position.metadata.properties.find(
            (position: any) => position.key === item
          ),
        ];
      });

      return filterData;
    },
    getPositionStatus: (state: any) => {
      if (state.position.isArchived) {
        return "Arkiverad";
      } else if (
        state.position.stop >= getYesterday().unix() &&
        state.position.start <= getCurrentUnixTime()
      ) {
        return "Öppen";
      } else {
        return "Stängd";
      }
    },
  },

  mutations: {
    loadPositionsCard(state: any, item: dtos.PositionResponse[]) {
      state.positionsCard = item;
    },
    loadPosition(state: any, item: dtos.PositionResponse) {
      state.position = item;
    },
    setPositionUser(state: any, item: PositionUser[]) {
      state.positionUsers = item;
    },
    setPositions(state: any, positions: any) {
      state.positions = positions;
      state.lastPositionLoaded = new Date().getSeconds();
    },
    setPositionApplicants(state: any, positionApplicants: any) {
      state.positionApplicants = positionApplicants;
    },
    setPositionApplicant(state: any, value: any) {
      state.positionApplicant = value;
    },
    loadPositionApplicant(state: any, value: ApplicantForm) {
      state.positionApplicant = value;
    },
    setApplicantFormBuilderAllItems(state: any, value: any) {
      state.positionApplicant.Items = value;
    },
    setPositionTemplate(state: any, value: PositionFormTemplate) {
      state.positionTemplate = value;
    },
    updatePositionTemplate(state: any, value: PositionFormTemplate) {
      const index = state.positionTemplates.findIndex(
        (px: PositionFormTemplate) => px.key === value.key
      );
      state.positionTemplates[index] = value;
    },
    deletePositionTemplate(state: any, value: PositionFormTemplate) {
      const index = state.positionTemplates.findIndex(
        (px: PositionFormTemplate) => px.key === value.key
      );
      state.positionTemplates.splice(index, 1);
    },
    resetPositionTemplatePositionTypes(
      state: any,
      value: {
        key: string;
        positionTypesSetAsDefault: {
          positionTypeId: number;
          positionTypeName: string;
          included: boolean;
          default: boolean;
        }[];
      }
    ) {
      state.positionTemplates.forEach((px: PositionFormTemplate) => {
        if (px.key !== value.key) {
          value.positionTypesSetAsDefault.forEach((py) => {
            const index = px.availableInPositionTypes.findIndex(
              (pz) => pz.positionTypeId === py.positionTypeId
            );
            px.availableInPositionTypes[index].default = false;
          });
        }
      });
    },
    setPositionApplicantInterestLabel(state: any, value: any) {
      const applicant = state.positionApplicants.find(
        (px: dtos.ApplicantPositionResponse) => px.id === value.applicantId
      );

      const currentLabel = applicant.labels.find(
        (px: any) => px.labelType.name === "Interest"
      );
      if (currentLabel.id !== value.label.id) {
        applicant.labels = applicant.labels.filter(
          (px: any) => px.labelType.name !== "Interest"
        );
        applicant.labels.push(value.label);
      }

      const index = state.positionApplicants.findIndex(
        (px: dtos.ApplicantPositionResponse) => px.id === value.applicantId
      );

      state.positionApplicants.splice(index, 1, applicant);
    },
    setApplicantMedflyttareStatus(state: any, value: any) {
      const index = state.positionApplicants.findIndex(
        (px: dtos.ApplicantPositionResponse) => px.id === value.applicantId
      );
      if (index === -1) return;
      const applicant = state.positionApplicants[index];
      applicant.isMedflyttare = value.isMedflyttare;
      applicant.medflyttare = value.isMedflyttare ? value.medflyttare : undefined;
      state.positionApplicants.splice(index, 1, applicant);
    },
    setDefaultFilter(state: any, value: any) {
      const index = state.positionFilters.findIndex(
        (px: dtos.PositionFilter) => px.positionId === state.position.id
      );

      if (index === -1) {
        state.positionFilters.push({
          positionId: state.position.id,
          defaultFilter: value,
          currentFilter: value,
        });
        return;
      }

      state.positionFilters[index] = {
        ...state.positionFilters[index],
        defaultFilter: value,
      };
    },
    setFilteredApplicantOrder(state: any, ids: Array<number>) {
      state.filteredApplicantOrder = ids;
    },
    setCurrentFilter(state: any, value: any) {
      const index = state.positionFilters.findIndex(
        (px: dtos.PositionFilter) => px.positionId === state.position.id
      );

      if (index === -1) {
        state.positionFilters.push({
          positionId: state.position.id,
          currentFilter: value,
        });
        return;
      }

      state.positionFilters[index] = {
        ...state.positionFilters[index],
        currentFilter: value,
      };
    },
    setCurrentFilterToDefaultFilter(state: any) {
      const index = state.positionFilters.findIndex(
        (px: dtos.PositionFilter) => px.positionId === state.position.id
      );
      state.positionFilters[index] = {
        ...state.positionFilters[index],
        currentFilter: state.positionFilters[index].defaultFilter,
      };
    },
    setCurrentSort(state: any, value: dtos.Sort) {
      const index = state.positionSortings.findIndex(
        (px: dtos.PositionFilter) => px?.positionId === state?.position?.id
      );

      if (index === -1) {
        state.positionSortings.push({
          positionId: state.position.id,
          currentSort: value,
        });
        return;
      }

      state.positionSortings[index] = {
        ...state.positionSortings[index],
        currentSort: value,
      };
    },
    setNumberOfApplicantsToRender(state: any, value: number) {
      state.positionApplicantsToRender = value;
    },
  },
  actions: {
    async addPositionTemplate(
      { state, getters, dispatch }: any,
      value: PositionTemplateAsset
    ) {
      const container = getters.container;
      const folder = getters.getPositionTemplateFolder;

      const data = new dtos.PositionTemplateRequest();
      data.availableInPositionTypes = value.data.availableInPositionTypes;
      data.createdBy = value.data.createdBy;
      data.createdAt = value.data.createdAt;
      data.description = value.data.description;
      data.key = value.data.key;
      data.metadata.properties = value.data.metadata.properties;
      data.name = value.data.name;

      const json = JSON.stringify(data);

      const assetResponse = await client.Container.createContainerAsset(
        0,
        container.id,
        folder.id,
        json,
        "application/json",
        value.name
      );

      dispatch(
        "addNotification",
        new ToastNotification("success", "Mall sparad")
      );

      // state.positionTemplates.push(value);
    },
    addPositionTemplateState(
      { getters, state }: any,
      v: PositionTemplateAsset
    ) {
      const entity = state.positionTemplates?.find(
        (x: any) => x.name === v.name
      );
      if (!entity) {
        const container = getters.container;
        const x = new PositionFormTemplate(
          v,
          container,
          null,
          container.positionTypes
        );
        x.description = v.data.description;
        x.name = v.data.name;
        state.positionTemplates.push(x);
      }
    },
    async loadPosition({ commit, getters, dispatch }: any, id: number) {
      const item = await client.JobPosition.getById(id);

      if (getters.container.id === undefined) {
        await dispatch("init");
      }

      const container = new MainContainer(getters.container);
      const users = new Array<PositionUser>();
      item.userPositions.forEach((element) => {
        const user = new PositionUser(element.user, container, item);
        const exist = users.find((px) => {
          return px.Email === user.Email;
        });
        if (typeof undefined === typeof exist) users.push(user);
      });
      container.DTO.users.forEach((element) => {
        const positionUser = users.find((px) => px.UserId === element.userId);
        if (typeof undefined === typeof positionUser) {
          const containerUser = new PositionUser(element, container, item);
          users.push(containerUser);
        }
      });
      container.DTO.users;
      commit("loadPosition", item);
      commit("setPositionUser", users);
    },
    async loadPositionsCard({ commit, getters }: any, id: number) {
      const item = await client.JobPosition.getPositionsCard(id);
      commit("loadPositionsCard", item);
    },
    async createPosition(
      { commit, getters, dispatch }: any,
      item: dtos.PositionRequest
    ) {
      item.containerId = getters.container.id;
      if (item.id > 0) {
        await client.Container.updatePosition(item);
        dispatch("addNotification", new ToastNotification("success", "Sparad"));
      } else {
        await client.Container.createPosition(item).then((response) => {
          dispatch("createPositionNotification", {
            positionId: response,
            positionName: item.name,
            positionPublishDate: item.start,
          });
          dispatch(
            "addNotification",
            new ToastNotification("success", "Sparad")
          );
        });
      }
      router.push("/admin/positions");
    },
    async copyPosition(
      { commit, getters, dispatch }: any,
      item: dtos.PositionRequest
    ) {
      item.containerId = getters.container.id;
      if (item.id > 0) {
        await client.Container.createPosition(item).then((response) => {
          dispatch("createPositionNotification", {
            positionId: response,
            positionName: item.name,
            positionPublishDate: item.start,
          });
          dispatch(
            "addNotification",
            new ToastNotification("success", "Sparad")
          );
        });
        dispatch("addNotification", new ToastNotification("success", "Sparad"));
      }
      router.push("/admin/positions");
    },
    async createInternalPosition(
      { commit, getters, dispatch }: any,
      item: dtos.PositionRequest
    ) {
      item.containerId = getters.container.id;
      if (item.id > 0) {
        await client.Container.updateInternalPosition(item);
        dispatch("addNotification", new ToastNotification("success", "Sparad"));
      } else {
        await client.Container.createInternalPosition(item).then((response) => {
          dispatch("createPositionNotification", {
            positionId: response,
            positionName: item.name,
            positionPublishDate: item.start,
          });
          dispatch(
            "addNotification",
            new ToastNotification("success", "Sparad")
          );
        });
      }
      router.push("/admin/positions");
    },
    async updatePosition(
      { commit, getters, dispatch }: any,
      item: dtos.PositionRequest
    ) {
      await client.Container.updatePosition(item)
        .then((response) => {
          dispatch(
            "addNotification",
            new ToastNotification("success", "Sparad")
          );
        })
        .catch((error) => {});
    },
    async updateInternalPosition(
      { commit, getters, dispatch }: any,
      item: dtos.PositionRequest
    ) {
      await client.Container.updateInternalPosition(item)
        .then((response) => {
          dispatch(
            "addNotification",
            new ToastNotification("success", "Sparad")
          );
        })
        .catch((error) => {});
    },
    async getAllApplicantsOnPosition(
      { commit, getters }: any,
      { containerId, positionId, filter, filterInclude }: any
    ) {
      let applicants = [] as dtos.ApplicantPositionResponse[];
      const labels = getters.getLabelInteres.filter(
        (label: any) => label.labelType.name === "Interest"
      );
      // for (const label of labels) {
      //   const labelId = label.id;
      //   const applicantsWithLabel =
      //     (await client.JobPosition.getApplicantPositionByLabelId(
      //       containerId,
      //       labelId,
      //       positionId,
      //       filter,
      //       filterInclude
      //     )) as any;
      //   applicants = [...applicants, ...applicantsWithLabel.data];
      // }
      // const labelPromises = labels.map((label: any) => {
      //   const labelId = label.id;
      //   return client.JobPosition.getApplicantPositionByLabelId(
      //     containerId,
      //     labelId,
      //     positionId,
      //     filter,
      //     filterInclude
      //   );
      // });
      // const results = await Promise.all(labelPromises);
      // applicants = results.flatMap((result) => result.data);
      const applicantsWithLabel =
        (await client.JobPosition.getApplicantPositionByLabelId(
          containerId,
          -1,
          positionId,
          filter,
          filterInclude
        )) as any;
      applicants = [...applicants, ...applicantsWithLabel.data];
      commit("setPositionApplicants", applicants);

      return applicants;
    },
    async getApplicantPositionsByLabel(
      { commit }: any,
      { containerId, labelId, positionId, filter, filterInclude }: any
    ) {
      let applicants = [] as dtos.ApplicantPositionResponse[];

      const applicantsWithLabel =
        (await client.JobPosition.getApplicantPositionByLabelId(
          containerId,
          labelId,
          positionId,
          filter,
          filterInclude
        )) as any;

      applicants = [...applicants, ...applicantsWithLabel.data];

      commit("setPositionApplicants", applicants);

      return applicants;
    },
    async setApplicantsLabel({ commit, getters }: any, { labelId, ids }: any) {
      await client.JobPosition.updateJobApplicationStatus(labelId, ids);
      ids.forEach((id: number) => {
        commit("setPositionApplicantInterestLabel", {
          applicantId: id,
          label: getters.getLabelInteres.find((px: any) => px.id === labelId),
        });
      });
    },
    async setApplicantMedflyttare(
      { commit }: any,
      { applicantPositionId, isMedflyttare, status, note, relocationDate }: any
    ) {
      await client.JobPosition.setApplicantMedflyttare(
        applicantPositionId,
        isMedflyttare,
        status,
        note,
        relocationDate
      );
      commit("setApplicantMedflyttareStatus", {
        applicantId: applicantPositionId,
        isMedflyttare: isMedflyttare,
        medflyttare: {
          isActive: isMedflyttare,
          status: status,
          note: note,
          relocationDate: relocationDate,
        },
      });
    },

    async loadPositions(
      { state, commit, getters, rootGetters }: any,
      id: number
    ) {
      const response = await client.Container.getPositions(id);
      commit("setPositions", response);
    },
    async updatePositionPermission(
      { commit, dispatch }: any,
      item: dtos.PositionPermission
    ) {
      await client.Container.createPositionPermission(item);
      dispatch("addNotification", new ToastNotification("success", "Sparad"));
    },
    async updatePositionPermissions(
      { commit, dispatch }: any,
      item: dtos.PositionPermissionRequest
    ) {
      await client.Container.updatePositionPermission(item);
      // dispatch("addNotification", new ToastNotification("success", "Sparad"));
    },
    async loadPositionApplicant({ commit }: any, id: any) {
      const item = await client.JobPosition.getApplicantPosition(id);
      const builder = new FormBuilder2(item.metadata, false);
      const applicant = new ApplicantForm(builder.Items, item);
      commit("loadPositionApplicant", applicant);
    },
    async loadPositionApplicants(
      { commit }: any,
      { positionId, labelId }: any
    ) {
      const pId = parseInt(positionId);
      const response = await client.JobPosition.getApplicantsByPosition(
        pId,
        labelId
      );
      commit("setPositionApplicants", response);
    },
    async createApplicantNote({ dispatch }: any, { id, note }: any) {
      await client.JobPosition.createApplicantNote(id, note);

      dispatch(
        "addNotification",
        new ToastNotification("success", "Anteckning sparad")
      );
    },
    async deleteApplicantFromPosition(
      { dispatch }: any,
      { positionId, applicantId }: any
    ) {
      await client.JobPosition.deleteJobapplication(positionId, applicantId);

      dispatch(
        "addNotification",
        new ToastNotification("success", "Ansökan borttagen")
      );
    },
    async sendEmailToOneRecipient(
      { dispatch }: any,
      { applicantId, email, subject, body }: SendEmailToOneRecipientRequest
    ) {
      try {
        await client.JobPosition.sendEmailToOneRecipient({
          applicantId,
          email,
          subject,
          body,
        });

        dispatch(
          "addNotification",
          new ToastNotification("success", "E-post skickad")
        );
      } catch (error: any) {
        console.error(error);
      }
    },
    async sendEmailToApplicants(
      { dispatch }: any,
      { positionId, recipients, subject, body }: SendEmailRequest
    ) {
      try {
        await client.JobPosition.sendEmailToApplicants({
          positionId,
          recipients,
          subject,
          body,
        });
      } catch (error: any) {
        console.error(error);
      }
    },
    async loadPositionTemplate({ commit, getters }: any, id: string) {
      const template = getters.getPositionTemplates.find(
        (x: PositionFormTemplate) => x.key === id
      );

      if (template) {
        commit("setPositionTemplate", template);
      } else {
        console.error("Template not found");
      }

      return template;
    },
    async updatePositionTemplate(
      { dispatch, commit }: any,
      template: PositionFormTemplate
    ) {
      commit("updatePositionTemplate", template);
      dispatch(
        "addNotification",
        new ToastNotification("success", "Mall sparad")
      );
    },
    async deletePositionTemplate(
      { dispatch, commit }: any,
      template: PositionFormTemplate
    ) {
      await client.Container.deleteContainerAsset(
        template.PositionTemplateAsset.containerId,
        template.PositionTemplateAsset.folderId,
        template.PositionTemplateAsset.id
      );

      commit("deletePositionTemplate", template);
      await dispatch("loadContainerFolders");

      dispatch(
        "addNotification",
        new ToastNotification("success", "Mall borttagen")
      );
    },
    async resetPositionTemplatePositionTypes(
      { commit }: any,
      template: PositionFormTemplate
    ) {
      const key = template.key;
      const positionTypesSetAsDefault =
        template.availableInPositionTypes.filter(
          (positionType) => positionType.default
        );

      commit("resetPositionTemplatePositionTypes", {
        key,
        positionTypesSetAsDefault,
      });
    },
    async archivePosition(
      { state, commit }: any,
      position: dtos.PositionResponse
    ) {
      await client.JobPosition.archivePosition(state.position.id, true);
      commit("loadPosition", position);
    },
    async reActivatePosition(
      { state, commit }: any,
      position: dtos.PositionResponse
    ) {
      await client.JobPosition.archivePosition(state.position.id, false);
      commit("loadPosition", position);
    },
    async changeNumberOfApplicantsToRender(
      { commit }: any,
      numberOfApplicantsToRender: number
    ) {
      commit("setNumberOfApplicantsToRender", numberOfApplicantsToRender);
    },
    async getOriginalPositionFilter(
      { state, getters, commit }: any,
      options: {
        position: dtos.PositionResponse;
        hideStatus?: boolean;
        viewAppliedPositions?: boolean;
      }
    ) {
      if (options.position.id !== state.position.id) {
        commit("loadPosition", options.position);
      }

      const filter = [] as dtos.FilterCategory[];

      if (
        getters.getLabelInteres &&
        getters.getLabelInteres.length > 0 &&
        !options?.hideStatus
      ) {
        const statusData = [];
        statusData.push({
          key: "status_all",
          name: "Alla",
          active: true,
        });
        getters.getLabelInteres.forEach((label: any) => {
          statusData.push({
            key: label.id.toString(),
            name: label.name,
            active: true,
          });
        });
        filter.push({
          id: crypto.randomUUID(),
          name: "Status",
          displayName: "Status",

          data: statusData,
        });
      }
      if (getters.getLabelProcesses && getters.getLabelProcesses.length > 0) {
        const processData = [];
        processData.push({
          key: "process_all",
          name: "Alla",
          active: true,
        });
        getters.getLabelProcesses.forEach((label: any) => {
          processData.push({
            key: label.id.toString(),
            name: label.name,
            active: true,
          });
        });
        filter.push({
          id: crypto.randomUUID(),
          name: "Processnivå",
          displayName: "Processnivå",
          data: processData,
        });
      }

      const summerPositions = getters.getFilterDataFromMetadata(
        "summerpositionchoicesform"
      );

      if (summerPositions && summerPositions.length > 0) {
        const summerPositionsData = [];
        summerPositionsData.push({
          key: "position_all",
          name: "Alla",
          active: true,
        });
        summerPositions.forEach((label: any) => {
          summerPositionsData.push({
            key: label.key,
            name: label.label,
            active: true,
          });
        });
        filter.push({
          id: crypto.randomUUID(),
          name: "Tjänster",
          displayName: "Tjänster",
          data: summerPositionsData,
        });
      }

      const positionPeriods =
        getters.getFilterDataFromMetadata("summerperiodsform");

      if (positionPeriods && positionPeriods.length > 0) {
        const positionPeriodsData = [];
        positionPeriodsData.push({
          key: "period_all",
          name: "Alla",
          active: true,
        });
        positionPeriods.forEach((label: any) => {
          positionPeriodsData.push({
            key: label.key,
            name: label.label,
            active: true,
          });
        });
        filter.push({
          id: crypto.randomUUID(),
          name: "Perioder",
          displayName: "Perioder",
          data: positionPeriodsData,
        });
      }

      const driverLicense =
        getters.getFilterDataFromMetadata("driverlicenseform");

      if (driverLicense && driverLicense.length > 0) {
        const driverLicenseData = [] as dtos.FilterCategoryData[];
        driverLicenseData.push({
          key: "driverLicense_all",
          name: "Alla",
          active: false,
        });
        driverLicense.forEach((label: any) => {
          driverLicenseData.push({
            key: label.key,
            name: label.label,
            active: false,
          });
        });
        // filter.push({
        //   id: crypto.randomUUID(),
        //   name: "Har körkort",
        //   displayName: "Har körkort",
        //   data: driverLicenseData,
        // });
      }

      const licenseToDrive =
        getters.getFilterDataFromMetadata("licensetodriveform");

      if (licenseToDrive && licenseToDrive.length > 0) {
        const driverLicenseData = [] as dtos.FilterCategoryData[];
        driverLicenseData.push({
          key: "licenseToDrive_all",
          name: "Alla",
          active: false,
        });
        licenseToDrive.forEach((label: any) => {
          driverLicenseData.push({
            key: label.key,
            name: label.label,
            active: false,
          });
        });
        filter.push({
          id: crypto.randomUUID(),
          name: "Har körkort och egen bil",
          displayName: "Har körkort och egen bil",
          data: driverLicenseData,
        });
      }

      const requirementGroups = state.position.metadata.properties.filter(
        (item: any) =>
          item.key.includes("requirementsform") && item.type === "group"
      );
      requirementGroups.forEach((element: dtos.DynamicProperty) => {
        const requirements = getters.getFilterDataFromMetadata(element.key);
        if (requirements && requirements.length > 0) {
          const requirementsData = [];
          requirementsData.push({
            key: requirements.key,
            name: "Alla",
            active: false,
          });
          requirements.forEach((label: any) => {
            requirementsData.push({
              key: label.key,
              name: label.label,
              active: false,
            });
          });
          filter.push({
            id: crypto.randomUUID(),
            name: "Meriterande",
            displayName: element.placeholder,
            data: requirementsData,
          });
        }
      });

      const checkBoxGroups = state.position.metadata.properties.filter(
        (item: any) =>
          !item.key.includes("requirementsform") &&
          item.key != "form_agreement" &&
          item.type === "group"
      );
      checkBoxGroups.forEach((element: dtos.DynamicProperty) => {
        const requirements = getters.getFilterDataFromMetadata(element.key);
        if (requirements && requirements.length > 0) {
          const requirementsData = [];
          requirementsData.push({
            key: requirements.key,
            name: "Alla",
            active: false,
          });
          requirements.forEach((label: any) => {
            requirementsData.push({
              key: label.key,
              name: label.label,
              active: false,
            });
          });
          filter.push({
            id: crypto.randomUUID(),
            name: "CheckboxGroup",
            displayName: element.placeholder,
            data: requirementsData,
          });
        }
      });

      const radioGroups = state.position.metadata.properties.filter(
        (item: any) => item.type === "radioGroup"
      );

      radioGroups.forEach((element: dtos.DynamicProperty) => {
        const requirements = getters.getFilterDataFromMetadata(element.key);

        if (requirements && requirements.length > 0) {
          const requirementsData = [];
          requirementsData.push({
            key: requirements.key,
            name: "Alla",
            active: false,
          });
          requirements.forEach((label: any) => {
            requirementsData.push({
              key: label.key,
              name: label.label,
              active: false,
            });
          });
          filter.push({
            id: crypto.randomUUID(),
            name: "RadioGroup",
            displayName: element.placeholder,
            data: requirementsData,
          });
        }
      });

      if (options?.viewAppliedPositions && getters.positionsCard.length > 0) {
        const appliedPositionsData = [];
        appliedPositionsData.push({
          key: "applied_all",
          name: "Alla",
          active: true,
        });
        getters.positionsCard.forEach((label: any) => {
          appliedPositionsData.push({
            key: label.id.toString(),
            name: label.name,
            active: true,
          });
        });
        filter.push({
          id: crypto.randomUUID(),
          name: "Ansökta tjänster",
          displayName: "Ansökta tjänster",
          data: appliedPositionsData,
        });
      }

      return filter;
    },
  },
};
